/**
 * useArtifacts Hook
 *
 * Gerencia estado dos artefatos:
 * - Armazenamento em Map para acesso O(1)
 * - Suporte a atualizações com animação
 * - Controle de artefato ativo
 */

import { useState, useCallback } from 'react';
import type { Artifact } from '../../../types/chat';

// =============================================================================
// TYPES
// =============================================================================

interface ArtifactState {
  artifacts: Map<string, Artifact>;
  activeArtifact: Artifact | null;
  isUpdating: boolean;
}

interface UpdateArtifactOptions {
  preserveScroll?: boolean;
  animateTransition?: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export const useArtifacts = () => {
  const [state, setState] = useState<ArtifactState>({
    artifacts: new Map(),
    activeArtifact: null,
    isUpdating: false,
  });

  /**
   * Find artifact by identifier
   */
  const findArtifactById = useCallback(
    (id: string): Artifact | undefined => {
      return state.artifacts.get(id);
    },
    [state.artifacts]
  );

  /**
   * Add new artifact or update existing one
   */
  const addOrUpdateArtifact = useCallback(
    async (
      newArtifact: Artifact,
      options: UpdateArtifactOptions = {}
    ): Promise<'created' | 'updated'> => {
      let isUpdate = false;

      setState((prev) => {
        const existingArtifact = prev.artifacts.get(newArtifact.id);
        isUpdate = !!existingArtifact;

        if (isUpdate && options.animateTransition) {
          return { ...prev, isUpdating: true };
        }
        return prev;
      });

      if (isUpdate && options.animateTransition) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }

      setState((prev) => {
        const existingArtifact = prev.artifacts.get(newArtifact.id);
        const isUpdating = !!existingArtifact;
        const newArtifacts = new Map(prev.artifacts);

        const artifactToAdd = isUpdating
          ? { ...newArtifact, updatedAt: new Date().toISOString() }
          : newArtifact;

        newArtifacts.set(newArtifact.id, artifactToAdd);

        const newActiveArtifact =
          prev.activeArtifact?.id === newArtifact.id
            ? artifactToAdd
            : prev.activeArtifact;

        return {
          artifacts: newArtifacts,
          activeArtifact: newActiveArtifact,
          isUpdating: false,
        };
      });

      return isUpdate ? 'updated' : 'created';
    },
    []
  );

  /**
   * Set active artifact for panel display
   */
  const setActiveArtifact = useCallback((artifact: Artifact | null) => {
    setState((prev) => ({ ...prev, activeArtifact: artifact }));
  }, []);

  /**
   * Get all artifacts as array
   */
  const getAllArtifacts = useCallback((): Artifact[] => {
    return Array.from(state.artifacts.values());
  }, [state.artifacts]);

  /**
   * Clear all artifacts
   */
  const clearArtifacts = useCallback(() => {
    setState({
      artifacts: new Map(),
      activeArtifact: null,
      isUpdating: false,
    });
  }, []);

  /**
   * Remove artifact by id
   */
  const removeArtifact = useCallback((id: string) => {
    setState((prev) => {
      const newArtifacts = new Map(prev.artifacts);
      newArtifacts.delete(id);

      const newActiveArtifact =
        prev.activeArtifact?.id === id ? null : prev.activeArtifact;

      return {
        artifacts: newArtifacts,
        activeArtifact: newActiveArtifact,
        isUpdating: prev.isUpdating,
      };
    });
  }, []);

  return {
    artifacts: getAllArtifacts(),
    activeArtifact: state.activeArtifact,
    isUpdating: state.isUpdating,
    addOrUpdateArtifact,
    setActiveArtifact,
    findArtifactById,
    clearArtifacts,
    removeArtifact,
  };
};

export default useArtifacts;
